import SmartButton from 'src/common/components/Buttons/SmartButton'
import { useTranslation } from 'react-i18next'
import useBalance from 'src/common/contracts/hooks/token/useBalance'
import useActiveOrDefaultChain from 'src/common/hooks/web3/useActiveOrDefaultChain'
import TokenDisplay from 'src/common/TokenDisplay'
import Icon from 'src/common/ui-kit/Icon/Icon'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  offersToTakeAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  successfulTxHashAtom,
} from '../../stores/trade-store'
import useSwapTrade from './hooks/useSwapTrade'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import useSendTx from 'src/common/contracts/hooks/useSendTx'
import useExecuteTx, {
  TransactionResult,
} from 'src/common/contracts/hooks/useExecuteTx'
import TxStatusView from '../TxStatusView'
import { formatBuyTokenAmountMulti, formatSellTokenAmountMulti } from './utils'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'

export interface ReviewSwapProps {
  mode?: 'Swap' | 'Invest'
}

const ReviewSwap = ({ mode }: ReviewSwapProps) => {
  const { t } = useTranslation('trade')
  const navigate = useNavigate()
  const { smartAccountInfo } = useSmartAccount()

  const {
    takeOffersTx,
    needsApproval,
    loading: swapTradeLoading,
  } = useSwapTrade()

  const {
    sendTx: sendApproveTokenTx,
    isPending: isPendingApproveToken,
    status: statusApproveToken,
  } = useSendTx()

  const handleTxSuccess = ({ hash }: TransactionResult) => {
    setSuccessfulTxHash(hash)
    setSellTokenAmount(sellTokenAmount)
    setBuyTokenAmount(buyTokenAmount)
    navigate('/trade/swap/success')
  }

  const {
    executeTx,
    txStatus,
    isPending: isPendingTakeOffer,
  } = useExecuteTx({
    onSuccess: handleTxSuccess,
    transaction: takeOffersTx,
  })

  const takeOfferLoaderStatus = txStatus === 'error' ? 'pending' : txStatus

  const approveTokenLoaderStatus = isPendingApproveToken
    ? 'loading'
    : !needsApproval || statusApproveToken === 'success'
    ? 'success'
    : 'pending'

  const offersToTake = useAtomValue(offersToTakeAtom)

  const sellTokenAmount = formatSellTokenAmountMulti(offersToTake)
  const buyTokenAmount = formatBuyTokenAmountMulti(offersToTake)

  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const setSellTokenAmount = useSetAtom(sellTokenAmountAtom)
  const setBuyTokenAmount = useSetAtom(buyTokenAmountAtom)

  const setSuccessfulTxHash = useSetAtom(successfulTxHashAtom)

  const { balance, isLoading: balanceLoading } = useBalance({
    owner: smartAccountInfo?.address,
    token: sellToken,
  })

  const isBalanceEnough = balance?.gte(sellTokenAmount)

  const loading =
    swapTradeLoading ||
    balanceLoading ||
    isPendingApproveToken ||
    isPendingTakeOffer

  const disabled = !isBalanceEnough

  const handleBack = () => navigate(-1)

  const handlePlaceOrder = async () => {
    if (!takeOffersTx) return
    try {
      await executeTx()
    } catch (error) {
      console.error('Error placing order:', error)
    }
  }

  const offerFields =
    mode === 'Swap'
      ? [
          [t('reviewSwap.offersToTake'), offersToTake.length],
          // [t('reviewSwap.maxBuyingAmount'), displayAmount(buyTokenAmount)],
          // [
          //   t('reviewSwap.networkCost'),
          //   gasEstimateInEther
          //     ? `${gasEstimateInEther.toString()} ${chain.nativeCurrency?.symbol}`
          //     : null,
          // ],
          // [
          //   t('reviewSwap.totalToPay'),
          //   `${displayAmount(sellTokenAmount)} ${sellToken?.symbol}`,
          // ],
        ]
      : []

  return (
    <>
      <div className='paper p-4 mb-4'>
        <div className='flex items-center mb-4'>
          <Icon
            name='arrow-right'
            className='mr-4 rotate-180 cursor-pointer'
            onClick={handleBack}
          />
          <div className='typo-title2 text-main-accent'>
            {mode === 'Swap' ? 'Review Swap' : 'Review investment'}
          </div>
        </div>
        <div className='flex flex-col gap-2'>
          <TokenDisplay
            readonly
            token={sellToken}
            amount={sellTokenAmount}
            label={'Sell'}
          />
          <TokenDisplay
            readonly
            token={buyToken}
            amount={buyTokenAmount}
            label={'Buy'}
          />
        </div>
        {offerFields.length > 0 && (
          <>
            <hr className='my-4' />
            <div className='flex flex-col gap-2'>
              {offerFields.map(
                ([text, value]) =>
                  text && (
                    <div key={text} className='flex justify-between'>
                      <div className='typo-label1'>{text}</div>
                      <div className='typo-label1 text-main-accent'>
                        {value || <div className='loader w-3 h-3' />}
                      </div>
                    </div>
                  )
              )}
            </div>
          </>
        )}
        <hr className='my-4' />
        <TxStatusView
          approveSymbol={sellToken?.symbol}
          approveStatus={approveTokenLoaderStatus}
          mainStatus={takeOfferLoaderStatus}
        />
      </div>
      <SmartButton
        onClick={handlePlaceOrder}
        className='w-full'
        loading={loading}
        disabled={disabled}
      >
        {isBalanceEnough ? t('reviewSwap.placeOrder') : 'Insufficient balance'}
      </SmartButton>
    </>
  )
}

export default ReviewSwap
